import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import { FullScreenQuad } from "three/examples/jsm/postprocessing/Pass.js";
import { SobelEdgePass } from "./SobelEdgePass.js";
import { USE_GEOMETRY_EDGES, EDGE_RESOLUTION_SCALE } from "./config.js";
import debug from "debug";

const log = debug("app:postprocessing");

/**
 * Manages post-processing effects. When USE_GEOMETRY_EDGES is true,
 * Sobel is disabled and geometry-based edges are used instead.
 */
export default class PostProcessingManager {
  constructor(experience) {
    this.experience = experience;
    this.renderer = experience.renderer.instance;
    this.scene = experience.scene;
    this.camera = experience.camera.instance;
    this.sizes = experience.sizes;
    this.eventHub = experience.eventHub;

    this.enabled = false;
    this.edgesEnabled = false;
    this.is3DModeActive = false;

    this.setupComposer();
    this.setupEventListeners();
  }

  setupComposer() {
    const w = this.sizes.reSize.width;
    const h = this.sizes.reSize.height;
    const pixelRatio = Math.min(window.devicePixelRatio, 2);
    const renderW = Math.floor(w * pixelRatio);
    const renderH = Math.floor(h * pixelRatio);

    // Render target with MSAA for anti-aliasing (depthTexture not supported with samples)
    const renderTarget = new THREE.WebGLRenderTarget(renderW, renderH, {
      depthBuffer: true,
      type: THREE.HalfFloatType,
      samples: 4,
    });

    this.composer = new EffectComposer(this.renderer, renderTarget);
    this.composer.setSize(w, h);
    this.composer.setPixelRatio(pixelRatio);

    // Add render pass (renders the scene normally)
    this.renderPass = new RenderPass(this.scene, this.camera);
    this.composer.addPass(this.renderPass);

    // Models-only render target for depth (excludes bbox on layer 20)
    const modelsDepthTexture = new THREE.DepthTexture(renderW, renderH);
    this.modelsOnlyRT = new THREE.WebGLRenderTarget(renderW, renderH, {
      depthTexture: modelsDepthTexture,
      depthBuffer: true,
    });

    // Blurred depth for Sobel only - smooths depth to reduce dots; final image stays sharp
    this.blurredDepthRT = new THREE.WebGLRenderTarget(renderW, renderH, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
    });
    this.depthBlurMaterial = new THREE.ShaderMaterial({
      uniforms: {
        tDepth: { value: null },
        resolution: { value: new THREE.Vector2(renderW, renderH) },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D tDepth;
        uniform vec2 resolution;
        varying vec2 vUv;
        void main() {
          vec2 texel = vec2(1.0 / resolution.x, 1.0 / resolution.y);
          float d = 0.0;
          for (int i = -3; i <= 3; i++)
            for (int j = -3; j <= 3; j++)
              d += texture2D(tDepth, vUv + texel * vec2(float(i), float(j))).r;
          d /= 49.0;
          gl_FragColor = vec4(d, d, d, 1.0);
        }
      `,
    });
    this.depthBlurQuad = new FullScreenQuad(this.depthBlurMaterial);

    // Add Sobel edge detection pass (depth-only silhouette)
    this.sobelPass = new SobelEdgePass(new THREE.Vector2(renderW, renderH));
    this.sobelPass.renderToScreen = false; // Output goes to buffer for OutputPass
    this.sobelPass.enabled = false; // Will be set by syncInitialCheckboxState
    
    // Configure edge appearance
    this.sobelPass.setEdgeColor(0x000000); // Black edges
    this.sobelPass.setEdgeStrength(1.5); // Edge intensity
    this.sobelPass.setEdgeThreshold(0.1); // Sensitivity
    
    this.composer.addPass(this.sobelPass);

    // OutputPass applies tone mapping and color space so the result is not dark
    this.outputPass = new OutputPass();
    this.outputPass.renderToScreen = true;
    this.composer.addPass(this.outputPass);

    log("Post-processing composer initialized");
  }

  setupEventListeners() {
    // Listen for shadedWithEdges toggle
    this.eventHub.on("shadedWithEdges", (state) => {
      this.edgesEnabled = state.enabled;
      this.updateEffectState();
    });

    // Listen for 3D mode switch (edges are 3D-only)
    this.eventHub.on("3d-switch", (state) => {
      this.is3DModeActive = state.enabled;
      this.updateEffectState();
    });

    // Handle resize
    this.eventHub.on("resize", (newSize) => {
      this.resize(newSize);
    });

    // Update Sobel zoom uniform whenever camera changes (orbit/zoom/pan)
    const controls = this.experience.camera?.controls;
    if (controls) {
      controls.addEventListener("change", () => {
        if (this.enabled && this.sobelPass) {
          const edgeMult = this.sobelPass.setZoomFactor(this.camera.zoom);
          this.sobelPass.enabled = edgeMult >= 0.001;
        }
      });
    }

    // Initial state comes from shadedWithEdges and 3d-switch events (emitted by UIEventTracker)
  }

  /** Only active when BOTH edges checkbox is on AND 3D mode is active. Disabled when using geometry edges. */
  updateEffectState() {
    const shouldEnable =
      !USE_GEOMETRY_EDGES &&
      this.edgesEnabled &&
      this.is3DModeActive;
    this.enabled = shouldEnable;

    if (shouldEnable && this.sobelPass) {
      // Apply zoom-based values immediately (fixes initial load when edges are on by default)
      const edgeMult = this.sobelPass.setZoomFactor(this.camera.zoom);
      this.sobelPass.enabled = edgeMult >= 0.001;
    } else {
      this.sobelPass.enabled = shouldEnable;
    }

    if (!USE_GEOMETRY_EDGES) {
      log(`Sobel edges ${shouldEnable ? "ENABLED" : "DISABLED"} (3D-only)`);
    }

    this.eventHub.emit("needsRender", "true");
  }

  resize(newSize) {
    if (this.composer) {
      const pixelRatio = Math.min(window.devicePixelRatio, 2);
      const w = Math.floor(newSize.width * pixelRatio);
      const h = Math.floor(newSize.height * pixelRatio);

      this.composer.setSize(newSize.width, newSize.height);
      this.composer.setPixelRatio(pixelRatio);

      const rt = this.composer.renderTarget1;
      if (rt?.depthTexture) {
        rt.depthTexture.image.width = w;
        rt.depthTexture.image.height = h;
        rt.depthTexture.needsUpdate = true;
      }

      if (this.modelsOnlyRT?.depthTexture) {
        const edgeW = Math.floor(w * EDGE_RESOLUTION_SCALE);
        const edgeH = Math.floor(h * EDGE_RESOLUTION_SCALE);
        this.modelsOnlyRT.setSize(edgeW, edgeH);
        this.modelsOnlyRT.depthTexture.image.width = edgeW;
        this.modelsOnlyRT.depthTexture.image.height = edgeH;
        this.modelsOnlyRT.depthTexture.needsUpdate = true;
        this.sobelPass.setSize(edgeW, edgeH);
        if (this.blurredDepthRT) {
          this.blurredDepthRT.setSize(edgeW, edgeH);
          this.depthBlurMaterial.uniforms.resolution.value.set(edgeW, edgeH);
        }
      } else {
        this.sobelPass.setSize(w, h);
        if (this.blurredDepthRT) {
          this.blurredDepthRT.setSize(w, h);
          this.depthBlurMaterial.uniforms.resolution.value.set(w, h);
        }
      }
    }
  }

  render() {
    if (this.enabled && this.composer) {
      // Render models-only (layer 0) to separate RT for depth; bbox is on layer 20
      const cam = this.camera;
      const prevMask = cam.layers.mask;
      cam.layers.set(0);
      this.renderer.setRenderTarget(this.modelsOnlyRT);
      this.renderer.render(this.scene, cam);

      cam.layers.mask = prevMask;
      this.renderer.setRenderTarget(null);

      this.sobelPass.setExternalDepthTexture(this.modelsOnlyRT.depthTexture);
      const edgeMult = this.sobelPass.setZoomFactor(this.camera.zoom);
      // When zoomed out (mult≈0), skip Sobel pass but always run composer (keeps pipeline consistent)
      this.sobelPass.enabled = edgeMult >= 0.001;
      this.composer.render();
    } else {
      // Normal render (bypassing post-processing)
      this.renderer.render(this.scene, this.camera);
    }
  }

  cleanup() {
    log("Cleaning up post-processing...");
    
    if (this.modelsOnlyRT) this.modelsOnlyRT.dispose();
    if (this.blurredDepthRT) this.blurredDepthRT.dispose();
    if (this.depthBlurMaterial) this.depthBlurMaterial.dispose();
    if (this.depthBlurQuad) this.depthBlurQuad.dispose();
    if (this.composer) {
      this.composer.dispose();
    }
    if (this.sobelPass) {
      this.sobelPass.dispose();
    }
    if (this.outputPass) {
      this.outputPass.dispose();
    }

    this.eventHub.off("shadedWithEdges");
    this.eventHub.off("3d-switch");
    this.eventHub.off("resize");
    
    log("Post-processing cleanup complete");
  }
}
